import AnalyticsWebClient, {
	envType,
	tenantType,
	userType,
} from '@atlassiansox/analytics-web-client';

import type { WebStorageType } from '../../../services/web-storage-service/types';
import type { ConsentPreferences } from '../../../types';
import { getBscGlobalState } from '../bsc-global-state/core';
import { LOG_PREFIX } from '../logger/constants';
import { packageName, packageVersion } from '../package-info';

import { withAnalyticsEventSampling } from './with-sampling';

const getEnvironment = () => {
	const { hostname } = window.location;

	if (
		hostname.includes('.dev.') ||
		hostname.includes('.stg.') ||
		hostname.includes('.jira-dev.') ||
		hostname.includes('.jira-stg.')
	) {
		return envType.STAGING;
	}
	if (
		hostname.includes('localhost') ||
		hostname.includes('ngrok') ||
		hostname.includes('atlastunnel')
	) {
		return envType.DEV;
	}

	return envType.PROD;
};

// As many Atlassian products all have the same origin and we don't want the whole path for security reasons
// we'll extract the subpath for some additional context around our products if it exists
export const getProductSubPath = (path: string | null): string => {
	let subPath = '';

	if (!path) {
		return subPath;
	}
	const checkPath = (match: string) => {
		return new RegExp(`^${match}(/|$)`).test(String(path));
	};

	if (checkPath('/wiki')) {
		subPath = '/wiki';
	}
	if (checkPath('/jira')) {
		subPath = '/jira';
	}
	if (checkPath('/compass')) {
		subPath = '/compass';
	}
	if (checkPath('/canvas')) {
		subPath = '/canvas';
	}
	if (checkPath('/servicedesk') || checkPath('/jira/servicedesk')) {
		subPath = '/servicedesk';
	}

	return subPath;
};

const getProductFromDomain = (domain: string) => {
	if (domain === 'https://trello.com') {
		return 'trello';
	}

	return null;
};

export const getContextualPageDetails = (): {
	domain: string;
	product: string;
} => {
	const { origin, pathname } = window.location;
	const productPath = getProductSubPath(pathname);
	const domain = `${origin}${productPath}`;

	const product = getProductFromDomain(domain) || productPath.slice(1);

	return {
		domain: domain,
		product: product,
	};
};

const decorateEventAttributes = (
	attributes?:
		| OperationalEventAttributes
		| UIEventAttributes
		| ScreenEventAttributes
		| TrackEventAttributes,
) => {
	const { domain, product } = getContextualPageDetails();

	return {
		packageName,
		packageVersion,
		domain,
		product: getBscGlobalState().product?.toLowerCase() ?? product,
		...(attributes && attributes),
	};
};

export const getAnalyticsClient: () => InstanceType<typeof AnalyticsWebClient> | undefined =
	(() => {
		let instance: InstanceType<typeof AnalyticsWebClient> | undefined;
		return (): InstanceType<typeof AnalyticsWebClient> | undefined => {
			try {
				if (!instance && getBscGlobalState().analyticsEnabled) {
					instance = new AnalyticsWebClient(
						{
							env: getEnvironment(),
							product: 'atlassianCookies',
						},
						{
							// useLegacyUrl tells the analytics client to make calls directly to Global Analytics Service.
							// When set to false, the client will use a stargate proxy for the current URL to send events
							// to GAS. Said another way:
							// true => events are sent to https://api-private.stg.atlassian.com/gasv3/api/v1/batch
							// false => events are sent to <the current domain>/gasv3/api/v1/batch
							//
							// By default, this should be set to false.  If you need to validate that analytics events
							// are being transmitted correctly in local dev, you can temporarily enable it since localhost
							// won't be able to proxy to GAS via stargate.
							useLegacyUrl: false,
							disableCookiePersistence: true,
						},
					);
					if (getBscGlobalState().cloudId) {
						instance.setTenantInfo(tenantType.CLOUD_ID, getBscGlobalState().cloudId);
					}
					if (getBscGlobalState().userId) {
						instance.setUserInfo(userType.ATLASSIAN_ACCOUNT, getBscGlobalState().userId ?? '');
					}
				}
			} catch (e) {
				// Log  errors thrown in constructor initialization for the AnalyticsWebClient
				// eslint-disable-next-line no-console
				console.error(`${LOG_PREFIX}: Failed to initialize AnalyticsWebClient. ${e}`);
			}
			if (!getBscGlobalState().analyticsEnabled) {
				return undefined;
			}
			// Return the instance, even if it is undefined
			return instance;
		};
	})();

export type UICheckboxChangeAttributes = {
	isSelected: boolean;
};

export type UIEventAttributes = UICheckboxChangeAttributes | {};

export type ScreenEventAttributes = {};
export type TrackEventAttributes = {};

export type OperationalEventAttributes =
	| OperationalThirdPartyAttributes
	| OperationalPreferencesAttributes
	| OperationalCookieAttributes
	| OperationalSetCookieAttributes
	| OperationalStorageItemAttributes
	| OperationalSetStorageItemAttributes
	| OperationalBannerAppearanceAttributes
	| OperationalIndexedDBOpenAttributes
	| OperationalIndexedDBOpenStrictlyNecessaryAttributes
	| OperationalInitControlsParametersAttributes
	| OperationalSetItemWithoutInitAttributes
	| OperationalStorageCleanupCategorizedAttributes
	| OperationalStorageCleanupDeletedAttributes
	| {};

export interface OperationalThirdPartyAttributes {
	thirdParty: string;
	wasRejected: boolean;
}

export interface OperationalPreferencesAttributes {
	preferences: ConsentPreferences;
}

export interface OperationalCookieAttributes {
	cookieKey: string;
}

export interface OperationalSetCookieAttributes {
	cookieKey: string;
	wasRejected: boolean;
}
export interface OperationalStorageItemAttributes {
	itemKey: string;
}

export interface OperationalSetStorageItemAttributes {
	itemKey: string;
	wasRejected: boolean;
	storageType?: WebStorageType;
}

export interface OperationalBannerAppearanceAttributes {
	timeSinceDismissal: number;
}

export interface OperationalIndexedDBOpenAttributes {
	databaseName: string;
	wasRejected: boolean;
	itemCategory?: string;
}

export interface OperationalStorageCleanupCategorizedAttributes {
	/** Wall-clock duration of the full cleanupRevokedStorage call (classification only). */
	durationMs: number;
	deletionMode: string;
	revokedCount: number;
	uncategorizedCount: number;
	hitEnumerationLimit: boolean;
	/**
	 * True when a run's revoked-item count exceeds REVOKED_COUNT_CEILING. Can only be true while
	 * platform_bsc_increase_enumeration_limit is on — below the default cap it is unreachable.
	 */
	hitRevokedCountCeiling: boolean;
	/** Space-separated triples of `${storageType} ${itemKey} ${category}`. */
	revokedItems: string[];
	/** Space-separated pairs of `${storageType} ${itemKey}`. */
	uncategorizedItems: string[];
}

export interface OperationalStorageCleanupDeletedAttributes {
	deletedCount: number;
}

export interface OperationalIndexedDBOpenStrictlyNecessaryAttributes {
	databaseName: string;
}

export interface OperationalSetItemWithoutInitAttributes {
	itemKey: string;
	storageType: string;
}

export interface OperationalInitControlsParametersAttributes {
	flagEnabled?: boolean;
	webStorageEnabled?: boolean;
	indexedDbEnabled?: boolean;
	disableWebStorageConsentChecks?: boolean;
	disableIndexedDbConsentChecks?: boolean;
	allowUncategorizedCookiesInOverrides?: boolean;
	allowUncategorizedItemsInWebStorageOverrides?: boolean;
	perimeter?: string;
	hasShouldEnforceKeyCallback?: boolean;
}

export type UiActionSubjectIds =
	| 'preferences'
	| 'onlyNecessary'
	| 'acceptAll'
	| 'cookiesTrackingNotice'
	| 'confirm'
	| 'performanceConsent'
	| 'functionalConsent'
	| 'targetingConsent'
	| 'cancel'
	| 'close'
	| 'background';

export type OperationalEventAction =
	| 'bannerAppearanceAfterDismissal'
	| 'usedSetCookie'
	| 'usedSetCookieError'
	| 'usedGetCookie'
	| 'usedGetCookieError'
	| 'usedDocumentCookie'
	| 'usedDocumentCookieError'
	| 'usedSetStrictlyNecessaryCookie'
	| 'usedSetStrictlyNecessaryCookieError'
	| 'usedCheckThirdParty'
	| 'checkThirdPartyError'
	| 'usedDeleteCookie'
	| 'usedDeleteCookieError'
	| 'initializeControlsError'
	| 'loadStorageControlsError'
	| 'updatePreferencesError'
	| 'fetchConsentPreferencesError'
	| 'saveConsentPreferencesError'
	| 'usedAtlBrowserStorageGetItem'
	| 'usedAtlBrowserStorageGetItemError'
	| 'usedAtlBrowserStorageSetItem'
	| 'usedAtlBrowserStorageSetItemError'
	| 'usedAtlBrowserStorageSetStrictlyNecessaryItem'
	| 'usedAtlBrowserStorageSetStrictlyNecessaryItemError'
	| 'indexDbSetRecordError'
	| 'usedCheckIfSetByPackageError'
	| 'renderConsentBannerError'
	| 'usedAtlIndexedDBOpen'
	| 'usedAtlIndexedDBOpenError'
	| 'usedAtlIndexedDBOpenStrictlyNecessary'
	| 'usedAtlIndexedDBOpenStrictlyNecessaryError'
	| 'bscInitControlsParameters'
	| 'setItemWithoutInit'
	| 'storageCleanupCategorized'
	| 'storageCleanupDeleted'
	| 'storageCleanupError';

export type TrackEventActionSubject =
	| 'cookieConsentPreferences'
	| 'uncategorizedWebStorageItemSet'
	| 'browserExtensionKeysAllowed';

export type ScreenEventNames = 'cookieConsentModal' | 'cookieConsentBanner';

export function sendPackageOperationalEvent(properties: {
	action: OperationalEventAction;
	attributes?: OperationalEventAttributes;
}): void {
	withAnalyticsEventSampling(properties.action, () =>
		getAnalyticsClient()?.sendOperationalEvent({
			source: 'package',
			actionSubject: 'package',
			action: properties.action,
			attributes: decorateEventAttributes(properties.attributes),
		}),
	);
}

/**
 * Sends an operational event directly without sampling. Use this for error events that should
 * always be sent (sampling ratio = 1) and to avoid potential recursive call chains that can
 * occur when sampling config is loaded asynchronously via the same error-reporting code path.
 */
export function sendUnsampledPackageOperationalEvent(properties: {
	action: OperationalEventAction;
	attributes?: OperationalEventAttributes;
}): void {
	getAnalyticsClient()?.sendOperationalEvent({
		source: 'package',
		actionSubject: 'package',
		action: properties.action,
		attributes: decorateEventAttributes(properties.attributes),
	});
}

export function sendUIEvent(properties: {
	action: 'clicked';
	actionSubject: 'link' | 'button' | 'checkbox' | 'area';
	actionSubjectId: UiActionSubjectIds;
	source: 'cookieConsentModal' | 'cookieConsentBanner';
	attributes?: UIEventAttributes;
}): void {
	withAnalyticsEventSampling(properties.actionSubjectId, () =>
		getAnalyticsClient()?.sendUIEvent({
			...properties,
			attributes: decorateEventAttributes(properties.attributes),
		}),
	);
}

export function sendScreenEvent(properties: {
	name: ScreenEventNames;
	attributes?: ScreenEventAttributes;
}): void {
	withAnalyticsEventSampling(properties.name, () =>
		getAnalyticsClient()?.sendScreenEvent({
			...properties,
			attributes: decorateEventAttributes(properties.attributes),
		}),
	);
}

export function sendTrackEvent(properties: {
	source: 'cookieConsentModal' | 'cookieConsentBanner' | 'package';
	action: 'submitted' | 'package';
	actionSubject: TrackEventActionSubject;
	attributes?: TrackEventAttributes;
	// Runs only when the event passes sampling and is actually sent. Callers use this to record
	// that an event was emitted (e.g. per-session dedup bookkeeping). Not forwarded to the client.
	onSent?: () => void;
}): void {
	const { onSent, ...eventProperties } = properties;
	withAnalyticsEventSampling(properties.actionSubject, () => {
		const client = getAnalyticsClient();
		if (!client) {
			// No client means nothing was emitted, so we must not run onSent
			return;
		}
		client.sendTrackEvent({
			...eventProperties,
			attributes: decorateEventAttributes(properties.attributes),
		});
		onSent?.();
	});
}
